import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * shadcn/ui Button, tuned to "The Thread".
 * Pills on web (design-spec hard rule 6). In the referee flow coral is the referee's colour and
 * carries every CTA — except the one sage "Sign & send" action. Gold = seal/unlock only.
 * Focus ring is 2px sage-deep at 25% (design-spec/01).
 *
 * **The warm fills carry an `ink` label, not white** (founder-ratified 16 Jul 2026). White on
 * `coral` is 3.30:1 and on the gold gradient 2.24–2.97:1 — no token gets white to AA on either
 * hue, and a 14.5–16px semibold label is nowhere near WCAG "large". `ink` on the same,
 * *unchanged*, signed-off hues is 4.92:1 and 5.46–7.24:1, so the label gives way, not the brand.
 * Sage keeps white (4.85:1) — it never failed. See design-spec/01-foundations.md.
 *
 * That inverts the hover rule for the warm fills: `coral-deep` was the right hover under a white
 * label (darker = more contrast) but drops an ink label to 3.74:1. So coral now LIFTS on hover
 * like gold already does — brightness-105 → 5.29:1. `filter` is not a colour, so it is named
 * explicitly in the transition list or the hover would snap instead of easing.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill font-display font-medium leading-none transition-[color,background-color,border-color,filter] duration-ui ease-thread focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-sage-deep text-white shadow-btn-sage hover:bg-sage-hover',
        coral: 'bg-coral text-ink shadow-btn-coral hover:brightness-105',
        secondary: 'bg-sage-tint text-sage-text hover:bg-sage-tint/70',
        ghost: 'border border-line-2 bg-transparent text-ink hover:bg-paper-2',
        gold: 'bg-gradient-to-br from-[#DAA43F] to-gold text-ink shadow-ready hover:brightness-105',
      },
      size: {
        default: 'h-11 px-6 text-[14.5px]',
        sm: 'h-9 px-4 text-[14px]',
        lg: 'h-12 px-8 text-[16px]',
        block: 'h-12 w-full px-6 text-[15px]',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
